import { useContext } from "react";
import { useTranslation } from "react-i18next";
import type { Assets, PIMProduct } from "../../../types/types.PIM";
import { NewAssetFormV2 } from "../../../pages/SharedPages/ProductDetailPage/Assets/NewAssetFormV2";
import { getAssetName } from "../../../pages/SharedPages/ProductDetailPage/Assets/util";
import type { NewAssetFormSubmitValues } from "../../../pages/SharedPages/ProductDetailPage/Assets/util";
import type { DataMutate } from "../../../types/types";
import { Notifications } from "../../Notifications/NotificationsContext";
import { useStoreState } from "../../../util/util";
import {
  createDocumentAssetFromForm,
  getDocumentAssetErrorMessage,
} from "./documentAsset.utils";
import type { DocumentAttributeFormValue } from "./DocumentAttribute";

type AddDocumentToAttributeProps = {
  product?: PIMProduct;
  onAddDocuments: (docs: DocumentAttributeFormValue[]) => void;
  onCancel: () => void;
  uploadNewDocumentsOnly?: boolean;
  hideAccessibility?: boolean;
};

const stubProduct = {
  status: "archived",
  id: "",
} as PIMProduct;

export function AddDocumentAttribute({
  product,
  onAddDocuments,
  onCancel,
  uploadNewDocumentsOnly = false,
  hideAccessibility = false,
}: AddDocumentToAttributeProps) {
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();
  const { notifyError, notifySuccess } = useContext(Notifications);

  const handleNewAsset = async (values: NewAssetFormSubmitValues) => {
    try {
      const assetId = await createDocumentAssetFromForm(tenant_id, values);
      const content_type = values.file_type?.type || undefined;
      onAddDocuments([
        {
          asset_id: assetId,
          display_name:
            getAssetName(
              values.asset_source_mode,
              values.asset_name,
              values.external_asset
            ) || undefined,
          is_external: values.asset_source_mode === "external",
          host_name: values.asset_host?.label ?? "Agilis",
          asset_type: values.input_asset_type.value,
          content_type,
        },
      ]);
      notifySuccess(t("Asset successfully added"));
    } catch (error) {
      notifyError(
        getDocumentAssetErrorMessage(
          error,
          t("Could not add asset. Something went wrong.")
        ),
        { error }
      );
      throw error;
    }
  };

  const handleExistingAssets = async ({
    selected_assets,
  }: {
    selected_assets: Assets[];
  }) => {
    if (!selected_assets.length) {
      return;
    }
    onAddDocuments(
      selected_assets.map((asset) => ({
        asset_id: asset.id,
        display_name: asset.name,
        is_external: asset.asset_mode === "external",
        host_name: asset.host?.name ?? "Agilis",
        asset_type: asset.asset_type,
        content_type: asset.content_type ?? undefined,
        signed_url: asset.signed_url,
        preview_url: asset.preview_url,
        is_downloadable: asset.is_downloadable,
        visibility: asset.visibility,
        is_generated: asset.is_generated,
      }))
    );
    notifySuccess(t("Asset successfully added"));
  };

  return (
    <>
      <NewAssetFormV2
        title={t("Documents")}
        defaultAssetType="Documents"
        default_asset_types={[{ label: t("Document"), value: "document" }]}
        product={product ?? stubProduct}
        refreshParentData={(async () => undefined) as DataMutate<unknown>}
        onAddAssetData={handleNewAsset}
        onAddExistingAssets={
          uploadNewDocumentsOnly ? undefined : handleExistingAssets
        }
        onCancel={onCancel}
        uploadNewDocumentsOnly={uploadNewDocumentsOnly}
        hideAccessibility={hideAccessibility}
        showGeneratedAssets={false}
      />
    </>
  );
}
